import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useFetch } from "@/hooks/useFetch";
import { inr, num } from "@/lib/format";
import { Kpi, Loading, PageHeader, Section, StatusBadge } from "@/components/common/kit";
import DataTable from "@/components/common/DataTable";
import RemoteTable from "@/components/common/RemoteTable";
import { DateRangeFilter, useRange } from "@/components/common/Filters";
import { useAuth } from "@/context/AuthContext";
import { TRANSFER_COLS, RETURN_COLS } from "./Requests";
import { ADJ_COLS } from "./Adjustments";
import { EXP_COLS } from "./Expenses";
import { CLOSURE_COLS } from "./Closures";
import { MOVE_COLS } from "./Reports";

const STOCK_COLS = [
  { key: "name", label: "Product" }, { key: "sku", label: "SKU" }, { key: "qty", label: "Current Stock", type: "int" },
  { key: "pending_out", label: "Pending Out", type: "int" }, { key: "price", label: "Sales Price", type: "money" },
];
const ISSUE_COLS = [
  { key: "issue_no", label: "Issue ID" }, { key: "date", label: "Date", type: "date" },
  { key: "items", label: "Products", render: (r) => r.items.map((i) => `${i.name} ×${i.qty}`).join(", ") },
  { key: "total_packs", label: "Packs", type: "int" }, { key: "issued_by", label: "By" },
];

export default function SalesmanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { bizDate } = useAuth();
  const [range, setRange] = useRange("month");
  const { data } = useFetch(`/salesmen/${id}`);
  const { data: stock } = useFetch("/stock/current", { salesman_id: id });
  const { data: today } = useFetch(`/day/${id}/${bizDate}`);
  const { data: movement } = useFetch("/reports/stock-movement", { salesman_id: id, date_from: range.from, date_to: range.to });
  const { data: rec } = useFetch(`/receivables/${id}`);
  if (!data) return <Loading />;
  const sm = data.salesman;
  const p = { salesman_id: id, date_from: range.from, date_to: range.to };
  return (
    <div className="space-y-6">
      <PageHeader title={sm.full_name} subtitle={`${sm.code} · ${sm.mobile} · Emp ${sm.employee_code} · @${sm.username}`}
        actions={<><StatusBadge status={sm.status} /><Button variant="outline" onClick={() => nav(`/admin/day/${id}/${bizDate}`)} data-testid="view-today-button">Open today's day</Button></>} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Current Stock" value={`${num(data.stock_packs)} pks`} testid="detail-stock" />
        <Kpi label="Today's Packs Sold" value={num(today?.totals.packs_sold)} tone="blue" testid="detail-today-packs" />
        <Kpi label="Today's Sales" value={inr(today?.totals.sales_value)} tone="green" testid="detail-today-sales" />
        <Kpi label="Outstanding Receivable" value={inr(data.receivable.outstanding)} tone="red" testid="detail-outstanding" />
      </div>
      <div className="flex items-center gap-3"><span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Period</span><DateRangeFilter value={range} onChange={setRange} /></div>
      <Tabs defaultValue="stock">
        <TabsList className="flex h-auto flex-wrap justify-start">
          {["stock", "closures", "issues", "transfers", "returns", "shortages", "expenses", "receivables", "ledger"].map((t) => (
            <TabsTrigger key={t} value={t} className="capitalize" data-testid={`detail-tab-${t}`}>{t === "ledger" ? "Stock Ledger" : t}</TabsTrigger>
          ))}
        </TabsList>
        <Section className="mt-3">
          <TabsContent value="stock"><DataTable columns={STOCK_COLS} rows={stock?.items || []} totals={["qty", "pending_out"]} title={`${sm.full_name} current stock`} exportable testid="detail-stock-table" /></TabsContent>
          <TabsContent value="closures"><RemoteTable url="/closures" params={p} columns={CLOSURE_COLS} onRowClick={(r) => nav(`/admin/day/${id}/${r.date}`)} title={`${sm.full_name} closures`} testid="detail-closures-table" /></TabsContent>
          <TabsContent value="issues"><RemoteTable url="/issues" params={p} columns={ISSUE_COLS} testid="detail-issues-table" /></TabsContent>
          <TabsContent value="transfers"><RemoteTable url="/transfers" params={{ salesman_id: id }} columns={TRANSFER_COLS} testid="detail-transfers-table" /></TabsContent>
          <TabsContent value="returns"><RemoteTable url="/returns" params={{ salesman_id: id }} columns={RETURN_COLS} testid="detail-returns-table" /></TabsContent>
          <TabsContent value="shortages"><RemoteTable url="/adjustments" params={p} columns={ADJ_COLS} testid="detail-adj-table" /></TabsContent>
          <TabsContent value="expenses"><RemoteTable url="/expenses" params={p} columns={EXP_COLS} testid="detail-expenses-table" /></TabsContent>
          <TabsContent value="receivables">
            <p className="mb-3 text-sm">Due {inr(rec?.due)} · Settled {inr(rec?.settled)} · <b>Outstanding {inr(rec?.outstanding)}</b></p>
            <DataTable columns={[{ key: "date", label: "Date", type: "date" }, { key: "amount", label: "Amount", type: "money" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }]} rows={rec?.entries || []} searchable={false} testid="detail-rec-table" />
          </TabsContent>
          <TabsContent value="ledger"><DataTable columns={MOVE_COLS} rows={movement || []} title={`${sm.full_name} stock ledger`} exportable testid="detail-ledger-table" /></TabsContent>
        </Section>
      </Tabs>
    </div>
  );
}
